import { useState } from 'react'
import { Link } from 'react-router-dom'

import { FxIcon, FxPanel } from '@/components/fx'
import { API_BASE_URL } from '@/lib/api'

/**
 * "API-Zugang" — where to POST a scan for this form without the app.
 *
 * Read-only on purpose: **API keys are per user, not per template**. The
 * gateway has no template-scoped key and `/v1/admin/api-keys` (open to every
 * authenticated caller, scoped to themselves) is the only way to mint one,
 * so this section shows the address and points at the place that issues the
 * key rather than offering a "Schlüssel erzeugen" button this endpoint would
 * not honour.
 */
export function ApiAccessSection({ templateId }: { templateId: string }) {
  const [copied, setCopied] = useState(false)
  // An empty base means the app is served from the gateway's own origin.
  const base = API_BASE_URL || (typeof window === 'undefined' ? '' : window.location.origin)
  const endpoint = `${base}/v1/templates/${templateId}/documents`

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(endpoint)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 3000)
    } catch {
      // A browser that refuses the clipboard (no permission, no secure
      // context) leaves the address selectable, which is why it is text and
      // not an image.
      setCopied(false)
    }
  }

  return (
    <FxPanel title="API-Zugang" className="p-5 xs:p-8 sm:p-10">
      <p className="mb-3 mt-0 text-small text-text-muted">
        Dokumente für dieses Musterformular per Schnittstelle hochladen:
      </p>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-sm bg-ground px-3 py-2 text-small text-text">
          POST {endpoint}
        </code>
        <button
          type="button"
          onClick={copy}
          aria-label="Adresse kopieren"
          className="shrink-0 rounded-md p-2 text-text-muted hover:text-primary-text"
        >
          <FxIcon name="content_copy" size={20} />
        </button>
      </div>
      <p role="status" aria-live="polite" className="m-0 mt-1 min-h-[1.45em] text-small text-text-muted">
        {copied && 'Adresse kopiert'}
      </p>
      <p className="mb-0 mt-3 text-small text-text-muted">
        Der Schlüssel gehört zu Ihrem Konto, nicht zu diesem Formular. Sie verwalten ihn unter{' '}
        <Link to="/einstellungen" className="text-primary-text">
          Einstellungen
        </Link>
        .
      </p>
    </FxPanel>
  )
}
